import React, { useState } from "react";
import styles from "./SelectedRecipeCard.module.css";

// Import assets - Assuming these are already downloaded
import recipeImage from "../../assets/52c80c70af664c053cc40edc7663c3fc7ca4280e.png"; // Cheeseburger image
import timeIcon from "../../assets/537_11346.svg"; // Add time icon import

// Added imports for Popover and icons
import { Popover } from "@mantine/core";
import Plus from "../../assets/icons/Plus"; // Adjust path if needed
import Minus from "../../assets/icons/Minus"; // Adjust path if needed

// Define props for the component - replace with actual recipe data structure
interface SelectedRecipeCardProps {
  recipe: {
    id: string; // Assuming recipe has an ID
    name: string;
    imageUrl: string;
    // brandLogoUrl?: string; // Removed logo
    price: string;
    kcal?: string;
    grams?: string;
    discount?: string;
    tags?: string[]; // e.g., ['Spicy', '25 min', 'Easy to cook']
    quantity: number;
    difficulty?: string; // Add difficulty property
  };
  // Added quantity change handlers
  onIncreaseQuantity: (id: string) => void;
  onDecreaseQuantity: (id: string) => void;
}

const SelectedRecipeCard: React.FC<SelectedRecipeCardProps> = ({
  recipe,
  onIncreaseQuantity,
  onDecreaseQuantity,
}) => {
  // Use default images if specific ones aren't passed in props
  const displayImageUrl = recipe.imageUrl || recipeImage;
  // State for quantity popover visibility
  const [isQtyOpen, setIsQtyOpen] = useState(false);

  // Quantity display element reused in Popovers
  const quantityDisplay = (
    <div
      className={`${styles.quantityDisplay} ${
        isQtyOpen ? styles.quantityDisplayActive : ""
      }`}
      onClick={() => setIsQtyOpen((prev) => !prev)}
    >
      {recipe.quantity}
    </div>
  );

  return (
    <div className={styles.frame471Container}>
      {/* Image Container */}
      <div className={styles.imageContainer53711315}>
        <img
          src={displayImageUrl}
          alt={recipe.name}
          className={styles.recipeImage}
        />
        {/* Optional: Overlay border like 537:11314 if needed */}
        {/* <div className={styles.imageBorderOverlay}></div> */}
        {/* Optional: Dark corner like 537:11316 if needed */}
        {/* <div className={styles.imageCornerOverlay}></div> */}
      </div>

      {/* Content Container */}
      <div className={styles.contentContainer}>
        <p className={styles.recipeName53711309}>{recipe.name}</p>

        {/* Single Tags Row with discount and other tags */}
        <div className={styles.tagsRow}>
          {/* Only show discount tag if discount exists */}
          {recipe.discount && (
            <span className={`${styles.tagBase} ${styles.tagDiscount}`}>
              {recipe.discount}
            </span>
          )}

          {/* Other tags */}
          {(recipe.tags || []).map((tag, index) => {
            let styleClass = styles.tagGeneric; // Default tag style
            let tagContent: React.ReactNode = tag; // Use ReactNode type to fix the type error

            // Add time icon to time tags
            if (tag.toLowerCase().includes("min")) {
              styleClass = styles.tagTime;
              tagContent = (
                <>
                  <img
                    src={timeIcon}
                    alt="Time"
                    style={{
                      height: "14px",
                      marginRight: "4px",
                      verticalAlign: "middle",
                    }}
                  />
                  {tag}
                </>
              );
            }

            // Other tag types
            if (tag.toLowerCase().includes("spicy"))
              styleClass = styles.tagSpicy;

            return (
              <span key={index} className={`${styles.tagBase} ${styleClass}`}>
                {tagContent}
              </span>
            );
          })}
        </div>

        {/* Price and Quantity Row */}
        <div className={styles.priceRow}>
          {recipe.price ? (
            <span className={styles.priceText53711299}>{recipe.price}</span>
          ) : (
            <div
              className="neo-icon no-shadow"
              style={{
                height: "20px",
                width: "20px",
                display: "inline-flex",
                padding: 0,
                margin: 0,
              }}
            >
              <svg
                fill="none"
                stroke="var(--primary-color)"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                width="20"
                height="20"
                className="recipe-panel__loading-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                ></path>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Quantity Selector (copied & adapted from CartList) */}
      <div className={styles.quantitySelector}>
        <Popover
          position="top"
          opened={isQtyOpen}
          offset={{ mainAxis: 0, crossAxis: 0 }}
          width={30} // Match cart style
          onChange={setIsQtyOpen} // Directly use setter
        >
          <Popover.Dropdown className={styles.popoverDropdown}>
            <button
              className={styles.quantityButton}
              onClick={() => onIncreaseQuantity(recipe.id)}
            >
              <Plus />
            </button>
          </Popover.Dropdown>
          <Popover.Target>{quantityDisplay}</Popover.Target>
        </Popover>

        <Popover
          position="bottom"
          opened={isQtyOpen}
          offset={{ mainAxis: 0, crossAxis: 0 }}
          width={30}
        >
          {/* Invisible target reuse for positioning */}
          <Popover.Target>
            <span className={styles.popoverTargetHelper}>
              {quantityDisplay}
            </span>
          </Popover.Target>
          <Popover.Dropdown className={styles.popoverDropdown}>
            <button
              className={styles.quantityButton}
              onClick={() => onDecreaseQuantity(recipe.id)}
              disabled={recipe.quantity <= 1} // Disable if quantity is 1 or less
            >
              <Minus />
            </button>
          </Popover.Dropdown>
        </Popover>
      </div>
    </div>
  );
};

export default SelectedRecipeCard;
